import _ from 'lodash';
import moment from 'moment-timezone';
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { SubSection, SubSectionTitle, Text } from './styles';
import { TZ } from '../../../../shared/consts';
import Social from './social';

const Container = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 16px;

  ${props => props.theme.isMobileOrTablet && `
    flex-direction: column;
  `};
`;

const CareerSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  padding-right: 32px;

  ${props => props.theme.isMobileOrTablet && `
    padding-right: 0;
    width: 100%;
  `};
`;

const ExperienceSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
  padding-left: 32px;

  ${props => props.theme.isMobileOrTablet && `
    padding-left: 0;
    width: 100%;
  `};
`;

const RecentCareerPosition = styled.div`
  color: ${props => props.theme.secondaryColor};
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: 500;
  font-size: 16px;
  line-height: 120%;
`;

const RecentCareerPeriod = styled.div`
  color: #627484;
  font-family: ${props => props.theme.fontFamilyBody};
  font-weight: 500;
  font-size: 14px;
  line-height: 120%;

  ${props => props.theme.isMobileOrTablet && `
    border-bottom: 1px solid;
    border-color: #DBE1E8;
    padding-bottom: 16px;
  `};
`;

const today = moment.tz(TZ);

const buildJoinedAtLabel = input => {
  // input is already in ISO8601 format.
  // Do NOT use a parse string.
  const joined = moment.tz(input, TZ);
  const years = today.diff(joined, 'years');
  const months = today.diff(joined, 'months');
  const prefix = joined.format('MMMM YYYY');

  if (years > 0) {
    return `${prefix} (${years}+ years)`;
  }

  if (months > 0) {
    return `${prefix} (${months}+ months)`;
  }

  return `${prefix} (just joined)`;
};

const buildJoinedIndustryAtLabel = input => {
  // input is already in ISO8601 format.
  // Do NOT use a parse string.
  const joined = moment.tz(input, TZ);
  const years = today.diff(joined, 'years');
  const months = today.diff(joined, 'months');

  if (years > 0) {
    return `${years} years+ experience`;
  }

  if (months > 0) {
    return `${months} months+ experience`;
  }

  return 'Just started';
};

const buildRecentCareerPeriod = (joined, left) => {
  if (!joined) {
    return '';
  }

  // Inputs are already in ISO8601 format.
  // Do NOT use a parse string.
  const joinedAtStr = moment.tz(joined, TZ).format('MMMM YYYY');
  const leftAtStr = left ? moment.tz(left, TZ).format('MMMM YYYY') : 'Present';

  return `${joinedAtStr} to ${leftAtStr}`;
};

const buildSubSection = ({
  value, title, text, className,
}) => (!value ? null : (
  <SubSection key={title}>
    <SubSectionTitle>{title}</SubSectionTitle>
    <Text className={className}>{text || value}</Text>
  </SubSection>
));
buildSubSection.propTypes = {
  value: PropTypes.string,
  title: PropTypes.string.isRequired,
  text: PropTypes.string,
  className: PropTypes.string,
};
buildSubSection.defaultProps = {
  value: null,
  text: null,
  className: '',
};

export default function Career(props) {
  const {
    organization,
    position,
    accred,
    joinedCurrentRoleAt,
    joinedIndustryAt,
    countryName,
    cityName,
    recentCareer,
  } = props;

  const location = _.compact([cityName, countryName]).join(', ');

  const careers = [{
    value: organization, title: 'Company',
  }, {
    value: position, title: 'Position',
  }, {
    value: joinedCurrentRoleAt, title: 'Joined', text: buildJoinedAtLabel(joinedCurrentRoleAt),
  }];

  const experiences = [{
    value: joinedIndustryAt, title: 'Experience', text: buildJoinedIndustryAtLabel(joinedIndustryAt),
  }, {
    value: accred, title: 'Accreditation/Awards', className: 'multiline',
  }, {
    value: location, title: 'Location',
  }];

  return (
    <Container>
      <CareerSection>
        {careers.map(buildSubSection)}
        {recentCareer && (
          <SubSection>
            <SubSectionTitle>Previously</SubSectionTitle>
            <Text>{recentCareer.organisation}</Text>
            <RecentCareerPosition>{recentCareer.position}</RecentCareerPosition>
            <RecentCareerPeriod>
              {buildRecentCareerPeriod(recentCareer.joinedAt, recentCareer.leftAt)}
            </RecentCareerPeriod>
          </SubSection>
        )}
      </CareerSection>
      <ExperienceSection>
        {experiences.map(buildSubSection)}
        <Social {...props} />
      </ExperienceSection>
    </Container>
  );
}

Career.propTypes = {
  organization: PropTypes.string,
  position: PropTypes.string,
  accred: PropTypes.string,
  joinedCurrentRoleAt: PropTypes.string,
  joinedIndustryAt: PropTypes.string,
  countryName: PropTypes.string,
  cityName: PropTypes.string,
  recentCareer: PropTypes.shape({
    organisation: PropTypes.string,
    position: PropTypes.string,
    joinedAt: PropTypes.string,
    leftAt: PropTypes.string,
  }),
};

Career.defaultProps = {
  organization: null,
  position: null,
  accred: null,
  joinedCurrentRoleAt: null,
  joinedIndustryAt: null,
  countryName: null,
  cityName: null,
  recentCareer: null,
};
